import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { createImagingOrder } from '../../api/encounters.js'
import { searchImagingStudies } from '../../api/catalogues.js'
import OrgSelect from '../../components/OrgSelect.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CatalogueAutocomplete from './CatalogueAutocomplete.jsx'
import { Card, CardHeader } from '../../components/Card.jsx'
import { statusColor, formatDateTime } from '../../utils/format.js'
import { URGENCY } from '../../constants/index.js'
import OrderResultsPanel from './OrderResultsPanel.jsx'
import usePersistedState from '../../hooks/usePersistedState.js'

export default function ImagingOrderForm({ encounter, locked }) {
  const qc = useQueryClient()
  // Persist transient draft to localStorage so half-built imaging
  // orders survive HMR / tab close / re-mounts.
  const draftKey = `img:enc${encounter.id}`
  const [imagingId, setImagingId] = usePersistedState(`${draftKey}:centre`, null)
  const [indication, setIndication] = usePersistedState(`${draftKey}:indication`, '')
  const [items, setItems] = usePersistedState(`${draftKey}:items`, [])
  const [formError, setFormError] = useState(null)

  const mut = useMutation({
    mutationFn: (body) => createImagingOrder(encounter.id, body),
    onSuccess: (newOrder) => {
      // Splice the new imaging order into the encounter cache from the
      // POST response so the row appears instantly without waiting for
      // an invalidate + refetch round-trip.
      qc.setQueryData(['encounter', encounter.id], (prev) => {
        if (!prev || !newOrder) return prev
        return {
          ...prev,
          imaging_orders: [...(prev.imaging_orders || []), newOrder],
        }
      })
      qc.invalidateQueries({ queryKey: ['encounter', encounter.id] })
      setItems([]); setIndication('')
    },
  })

  function addStudy(s) {
    setItems((curr) => {
      if (curr.some((x) => x.study_code === s.code)) return curr
      return [...curr, {
        study_code: s.code, study_name: s.name, modality: s.modality || '',
        body_site: s.body_site || '', urgency: 'routine',
      }]
    })
  }
  function updateItem(i, patch) {
    setItems((curr) => curr.map((x, idx) => idx === i ? { ...x, ...patch } : x))
  }
  function removeItem(i) {
    setItems((curr) => curr.filter((_, idx) => idx !== i))
  }

  function onSubmit(e) {
    e.preventDefault()
    setFormError(null)
    if (!imagingId) return setFormError('Select a target imaging centre.')
    if (items.length === 0) return setFormError('Add at least one study.')
    mut.mutate({
      target_imaging_id: imagingId,
      clinical_indication: indication,
      items,
    })
  }

  const existing = encounter.imaging_orders || []

  return (
    <Card>
      <CardHeader title="Imaging orders" subtitle="Route to an imaging centre on submit" />

      {existing.length > 0 && (
        <div className="space-y-3 mb-4">
          {existing.map((io) => (
            <div key={io.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${statusColor(io.status)}`}>{io.status}</span>
                <span className="font-mono text-slate-500">#{io.id}</span>
                {io.radiology_center_name && (
                  <span className="text-slate-700"><span className="text-slate-400">→</span> {io.radiology_center_name}</span>
                )}
                {io.created_at && <span className="text-slate-400">· Ordered {formatDateTime(io.created_at)}</span>}
              </div>

              {/* Compact study list — keeps each card visually short on
                  encounter pages where the doctor scans many orders. */}
              {(io.items || []).length > 0 && (
                <div className="mt-2 text-sm text-slate-700">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Studies</span>
                  {io.items.map((it, i) => (
                    <span key={it.id || it.study_code || i}>
                      {i > 0 && <span className="text-slate-300 mx-1">·</span>}
                      <span className="font-medium">{it.study_name}</span>
                    </span>
                  ))}
                </div>
              )}

              {io.clinical_indication && (
                <div className="mt-2 text-[11px] text-slate-500">
                  <span className="uppercase tracking-wide text-slate-400">Clinical indication </span>
                  {io.clinical_indication}
                </div>
              )}

              {/*
                Report & attachments — radiology's typed
                report_summary plus any uploaded report PDFs and
                imaging files. Doctor sees them inline with Open +
                Download so they can review and update the encounter
                in one place.
              */}
              <OrderResultsPanel
                kind="imaging"
                encounter={encounter}
                orderId={io.id}
                status={io.status}
                resultStatus={io.result_status}
                summary={io.report_summary}
                prelim={io.preliminary_imaging_summary}
                impression={io.impression}
                comment={io.result_comment}
                ordered={(io.items || []).map((it) => ({ label: it.study_name }))}
                subtitle={io.radiology_center_name ? `Issued by ${io.radiology_center_name}` : 'Issued by the imaging centre'}
              />
            </div>
          ))}
        </div>
      )}

      {locked ? (
        <div className="text-xs text-slate-400">
          {existing.length === 0
            ? 'No imaging orders were recorded on this encounter.'
            : 'Encounter is locked. Add new orders via an amended encounter.'}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Target imaging centre</span>
            <div className="mt-1">
              <OrgSelect orgType="imaging" value={imagingId} onChange={setImagingId} placeholder="Select imaging centre…" />
            </div>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Add study</span>
            <div className="mt-1">
              <CatalogueAutocomplete
                queryKey="imaging-studies"
                searchFn={searchImagingStudies}
                placeholder="Search imaging studies (CXR, CT head, MRI knee, abdo US…)"
                rowKey={(s) => s.code}
                renderRow={(s) => (
                  <>
                    <span className="font-mono text-xs text-wantok-700">{s.short_code || s.code}</span>
                    <span className="ml-2 font-medium">{s.name}</span>
                    {s.body_region && <span className="ml-2 text-xs text-slate-500 italic">{s.body_region}</span>}
                    {s.modality && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-violet-50 text-violet-700 border-violet-200">{s.modality}</span>}
                    {s.contrast && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-800 border-amber-200">contrast</span>}
                    {s.requires_appointment && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-slate-50 text-slate-600 border-slate-200">appt</span>}
                    {s.typical_turnaround && <span className="ml-2 text-[10px] text-slate-500">TAT {s.typical_turnaround}</span>}
                  </>
                )}
                onPick={addStudy}
              />
            </div>
          </label>

          {items.length > 0 && (
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it.study_code} className="border border-slate-200 rounded p-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-xs text-wantok-700">{it.study_code}</span>
                    <span className="flex-1 truncate font-medium">{it.study_name}</span>
                    <select value={it.urgency} onChange={(e) => updateItem(i, { urgency: e.target.value })} className="border rounded px-1 py-1 text-xs">
                      {URGENCY.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <button type="button" onClick={() => removeItem(i)} className="text-xs text-rose-600">×</button>
                  </div>
                  {it.study_code && (
                    <ImagingAvailabilityHint studyId={it.study_code} centreId={imagingId} />
                  )}
                </div>
              ))}
            </div>
          )}

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Clinical indication</span>
            <textarea rows={2} value={indication} onChange={(e) => setIndication(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
          </label>

          {(formError || mut.error) && <ErrorBanner error={formError || mut.error} />}

          <div className="flex justify-end">
            <button disabled={mut.isPending} className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60">
              {mut.isPending ? 'Saving…' : 'Add imaging order'}
            </button>
          </div>
        </form>
      )}
    </Card>
  )
}

/**
 * Inline availability + price + TAT hint for a picked imaging study at
 * the chosen target radiology centre.
 */
function ImagingAvailabilityHint({ studyId, centreId }) {
  const stockQ = useQuery({
    queryKey: ['imaging-study-stock', studyId],
    queryFn: async () => {
      const { data } = await api.get(`${endpoints.imagingStudySearch}${studyId}/stock/`)
      return data
    },
    enabled: Boolean(studyId),
    staleTime: 30_000,
  })
  if (stockQ.isLoading) {
    return <div className="text-[11px] text-slate-400 italic">Checking imaging availability…</div>
  }
  if (stockQ.error || !Array.isArray(stockQ.data)) return null
  const rows = stockQ.data
  if (rows.length === 0) {
    return (
      <div className="text-[11px] bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1">
        No radiology centre on the platform currently offers this study.
        The order will still send; the receiving centre can run it as a
        send-out or refuse.
      </div>
    )
  }
  const target = centreId != null ? Number(centreId) : null
  const here = target != null ? rows.find((r) => Number(r.radiology_center) === target) : null
  const others = rows.filter((r) => Number(r.radiology_center) !== target)

  if (here) {
    return (
      <div className="text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-2 py-1 flex flex-wrap items-center gap-2">
        <span className="font-medium">✓ Offered at this centre</span>
        {here.price != null && <span>· PGK <b>{here.price}</b></span>}
        {here.turnaround_time && <span>· TAT <b>{here.turnaround_time}</b></span>}
        {here.requires_appointment && <span className="text-amber-700">· appointment required</span>}
      </div>
    )
  }
  return (
    <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">
      <div className="font-medium">
        ⚠ {centreId ? 'Not offered at the centre you picked.' : 'Pick a radiology centre to see availability.'}
        {others.length > 0 && ` Offered elsewhere by ${others.length} centre${others.length === 1 ? '' : 's'}:`}
      </div>
      {others.length > 0 && (
        <ul className="mt-1 ml-2">
          {others.slice(0, 5).map((r) => (
            <li key={r.id}>
              · {r.radiology_center_name}
              {r.price != null && <> — PGK {r.price}</>}
              {r.turnaround_time && <> · TAT {r.turnaround_time}</>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
